import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import Register from "./components/Auth/Register";
import LoginPage from "./components/Auth/LoginPage.js";
import Home from "./components/Home";
import QuestionPage from "./components/Posts/QuestionPage.js";
import CreatePost from "./components/Posts/CreatePostPage";
import TestComponent from "./components/TestComponent";
import Logout from "./components/Auth/Logout";
import People from "./components/Users/People.js";
import Landing from "./components/Landing";
import { useContext, useState } from "react";
import { AuthContext } from "./components/Auth/AuthProvider.js";
import UserProfile from "./components/Users/UserProfile";
import UserSettings from "./components/Users/UserSettings.js";
import Nav from "./components/Nav/Nav.js";
import { TopicProvider } from "./utils/TopicContext.js";
import UnSubscribe from "./components/Unsubscribe/UnSubscribe.js";
import { Topic } from "@mui/icons-material";
import ContactUs from "./components/ContactUs/ContactUs.js";
import { sendEmailVerification } from "firebase/auth";
import { motion } from "framer-motion";

const App = () => {
  const { user, loading } = useContext(AuthContext);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendMessage("");
    setResendError("");

    try {
      if (user && !user.emailVerified) {
        await sendEmailVerification(user);
        setResendMessage(
          "Verification email sent successfully! Please check your inbox and spam folder."
        );
      } else if (user && user.emailVerified) {
        console.log("User email is already verified");
        setResendError("Your email is already verified!");
      } else {
        console.log("No user found");
        setResendError("No user found. Please try logging in again.");
      }
    } catch (error) {
      console.error("Error sending verification email:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);

      // Provide user-friendly error messages
      if (error.code === "auth/too-many-requests") {
        setResendError(
          "Too many requests. Please wait a moment before trying again."
        );
      } else if (error.code === "auth/user-disabled") {
        setResendError(
          "This account has been disabled. Please contact support."
        );
      } else {
        setResendError(`Failed to send verification email: ${error.message}`);
      }
    } finally {
      setIsResending(false);
    }
  };

  function ProtectedRoutes({ children }) {
    if (loading) {
      return <div>Loading...</div>; // Or a spinner/loader
    }

    if (user) {
      return <>{children}</>;
    } else {
      return (
        <Navigate
          to="/login"
          state={{
            from: location.pathname,
          }}
          replace
        />
      );
    }
  }

  function EnsureEmailVerified({ children }) {
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 flex items-center space-x-4">
            <svg
              className="animate-spin h-8 w-8 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-gray-700 font-medium">Loading...</span>
          </div>
        </div>
      );
    }

    if (user && user.emailVerified) {
      return <>{children}</>;
    } else {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center space-y-6">
            {/* Email verification icon */}
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h1>
              <p className="text-gray-600">
                Please check your email and click the verification link to
                continue
              </p>
            </div>

            {/* Email display */}
            {user?.email && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">
                  Verification email sent to:
                </p>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
            )}

            {/* Success/Error Messages for Resend */}
            {resendMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm"
              >
                {resendMessage}
              </motion.div>
            )}

            {resendError && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
              >
                {resendError}
              </motion.div>
            )}

            {/* Instructions */}
            <div className="text-left space-y-3">
              <h3 className="font-semibold text-gray-900">Next steps:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    1
                  </span>
                  Check your email inbox (including spam folder)
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    2
                  </span>
                  Click the verification link in the email
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    3
                  </span>
                  Refresh this page or return to the app
                </li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              >
                I've Verified My Email
              </button>

              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isResending ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  "Resend Verification Email"
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  function LandingPageRedirect({ children }) {
    // if (loading) {
    //   return <div>Loading...</div>; // Or a spinner/loader
    // }
    return <>{children}</>;
    // if (user) {
    //   return <>{children}</>;
    // } else {
    //   return (
    //     <Navigate
    //       to="/landing"
    //       state={{
    //         from: location.pathname,
    //       }}
    //       replace
    //     />
    //   );
    // }
  }

  return (
    <div className="w-full h-full">
      <TopicProvider>
        <BrowserRouter>
          <Nav />

          <Routes>
            <Route
              path="/"
              element={
                <LandingPageRedirect>
                  <Home />
                </LandingPageRedirect>
              }
            />

            <Route
              path="/post"
              element={
                <ProtectedRoutes>
                  <CreatePost question={false} />
                </ProtectedRoutes>
              }
            />
            <Route
              path="/ask"
              element={
                <CreatePost question={true} />

                // <EnsureEmailVerified>
                //   <CreatePost question={true} />
                // </EnsureEmailVerified>
              }
            />
            {/* <Route
              path="/people"
              element={
                <EnsureEmailVerified>
                  <People />
                </EnsureEmailVerified>
              }
            /> */}
            <Route path="/profile/:user" element={<UserProfile />} />
            <Route path="/settings/:user" element={<UserSettings />} />
            <Route path="/unsubscribe/:token" element={<UnSubscribe />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/post/:postId" element={<QuestionPage />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/test" element={<TestComponent />} />
            <Route path="/contactus" element={<ContactUs />} />
          </Routes>
        </BrowserRouter>
      </TopicProvider>
    </div>
  );
};

export default App;
